import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, CheckCircle, ArrowLeft } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

/**
 * Detects if the user is using an in-app browser (TikTok, Instagram, Facebook, etc.)
 * These browsers have WebView limitations that can cause 3DS popups to fail
 */
const isInAppBrowser = (): boolean => {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';

  // TikTok browser detection
  const isTikTok = /TikTok/i.test(ua) || /BytedanceWebview/i.test(ua) || /musical_ly/i.test(ua);

  // Instagram browser detection  
  const isInstagram = /Instagram/i.test(ua);

  // Facebook browser detection
  const isFacebook = /FBAN|FBAV|FB_IAB|FBIOS/i.test(ua);

  // Snapchat browser detection
  const isSnapchat = /Snapchat/i.test(ua);

  // Twitter/X browser detection
  const isTwitter = /Twitter/i.test(ua);

  // WeChat browser detection
  const isWeChat = /MicroMessenger/i.test(ua);

  // LINE browser detection
  const isLine = /Line\//i.test(ua);

  // Generic WebView detection
  const isWebView = /wv|WebView/i.test(ua) ||
    (/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua)) ||
    (/Android.*Version\/[0-9].[0-9]/.test(ua) && !/Chrome\/[.0-9]*/.test(ua));

  const result = isTikTok || isInstagram || isFacebook || isSnapchat || isTwitter || isWeChat || isLine || isWebView;

  if (result) {
    console.log('📱 In-app browser detected:', {
      isTikTok, isInstagram, isFacebook, isSnapchat, isTwitter, isWeChat, isLine, isWebView,
      userAgent: ua.substring(0, 100) + '...'
    });
  }

  return result;
};

import OrderSummary from "../components/OrderSummary";
import PayPalPaymentForm from "../components/PayPalPaymentForm";
import { useOrder, useAuth, useOrgaConfigurationContext } from "@/app/providers";
import { createPaymentIntentDirect, FirebaseOrderService } from "@/services";
import { getStripePublicKey } from "@/shared/config/stripe";
import { useNavigation } from "@/shared/hooks/useNavigation";
import { useOrderJourneySecurity } from "@/shared/hooks/useOrderJourneySecurity";
import { useOrderPersonalization } from "@/shared/hooks/useOrderPersonalization";
import SeoTags from '@/components/shared/SeoTags';

// Enable Developer Tools (Sandbox Assistant) only in development/local mode
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

// Helper pour appliquer une couleur ou un gradient




const CheckoutForm: React.FC<{ clientSecret: string }> = ({ clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { organizationId, navigateToConfirmation } = useNavigation();
  const { orderData, setIsOrderComplete, isMergedOrder } = useOrder();
  const { t } = useTranslation();
  const personalization = useOrderPersonalization();

  // Security check for organization consistency
  const { hasOrganizationChanged } = useOrderJourneySecurity();

  const [processing, setProcessing] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Force button styles when stripe or processing state changes
  useEffect(() => {
    const button = buttonRef.current;
    if (button) {
      if (!stripe || processing) {
        // Disabled state
        const textColor = personalization.button.disabledText || '#6B7280';
        button.style.setProperty('color', textColor, 'important');
        button.style.setProperty('background-color', personalization.button.disabledBackground || '#D1D5DB', 'important');
        button.style.removeProperty('background');
        button.style.removeProperty('background-image');
      } else {
        // Active state - force colors with !important
        const textColor = personalization.button.primaryText || '#FFFFFF';
        const bgColor = personalization.button.primary || '#9333EA';

        button.style.setProperty('color', textColor, 'important');

        if (bgColor.includes('gradient') || bgColor.includes('linear-gradient')) {
          button.style.setProperty('background', bgColor, 'important');
          button.style.removeProperty('background-color');
        } else {
          button.style.setProperty('background-color', bgColor, 'important');
          button.style.removeProperty('background');
          button.style.removeProperty('background-image');
        }
      }
    }
  }, [stripe, processing, personalization.button.primary, personalization.button.primaryText, personalization.button.disabledBackground, personalization.button.disabledText]);

  // Check if user is in an in-app browser
  const [showInAppWarning, setShowInAppWarning] = useState(false);

  useEffect(() => {
    const inApp = isInAppBrowser();
    setShowInAppWarning(inApp);

    // Log for debugging
    if (inApp) {
      console.log('⚠️ Payment form loaded in in-app browser - redirect mode will be used for 3DS');
    }
  }, []);

  // Calculate total amount - only current product for merged orders
  const getSubtotal = () => {
    // For merged orders, only calculate amount for the last product (the one being paid for)
    if (isMergedOrder && orderData.products && orderData.products.length > 0) {
      const currentProduct = orderData.products[orderData.products.length - 1];
      return parseFloat(currentProduct.amount || "0");
    }

    // For new orders, calculate total from all products
    if (orderData.products && orderData.products.length > 0) {
      return orderData.products.reduce((total, product) => {
        return total + parseFloat(product.amount || "0");
      }, 0);
    } else {
      return parseFloat(orderData.amount || "0");
    }
  };

  const getShippingCost = () => {
    // If no delivery method selected, return 0
    if (!orderData.deliveryMethod) return 0;

    // Use the shipping cost from orderData (set when method was selected)
    // This comes from Sendcloud/configuration, not hardcoded values
    return orderData.shippingCost || 0;
  };

  // For merged orders, shipping was already paid, so don't charge again
  const shippingCost = isMergedOrder ? 0 : getShippingCost();
  const totalAmount = getSubtotal() + shippingCost;

  const handlePayment = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      toast.error(t("payment.errors.stripeNotLoaded"));
      return;
    }

    setProcessing(true);

    // Detect if we're in an in-app browser (TikTok, Instagram, etc.)
    const inAppBrowser = isInAppBrowser();

    // Add body class for CSS styling during 3DS
    document.body.classList.add('stripe-3ds-active');

    try {
      // First, trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(
          t("payment.errors.validation", { message: submitError.message }),
        );
        setProcessing(false);
        document.body.classList.remove('stripe-3ds-active');
        return;
      }

      if (inAppBrowser) {
        console.log('📱 In-app browser detected - using if_required redirect strategy');
      }

      // Confirm the payment with the client secret
      // Toujours utiliser if_required pour éviter les redirections forcées
      // dans les navigateurs intégrés (ex: TikTok WebView).
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/orga/${organizationId}/order/confirmation`,
        },
        redirect: "if_required",
      });

      // Remove body class
      document.body.classList.remove('stripe-3ds-active');

      if (error) {
        console.error("Payment failed:", error);

        // Special handling for 3DS authentication errors in WebViews
        if (error.type === 'card_error' && error.code === 'payment_intent_authentication_failure') {
          console.log('❌ 3DS authentication failed - possibly WebView issue');
          toast.error(t("payment.errors.authenticationFailed") || "Authentication failed. Please try again or use a different payment method.");
        } else {
          toast.error(
            t("payment.errors.payment", { message: error.message ?? "" }),
          );
        }
      } else {
        // Payment succeeded - no paymentIntent object is returned when redirect is 'if_required'
        console.log("✅ Payment succeeded");

        // Show success message
        toast.success(t("payment.success.message"));

        // Mark order as complete so confirmation page can access the data
        setIsOrderComplete(true);

        // Navigate to confirmation page immediately
        navigateToConfirmation();
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(t("payment.errors.generic"));
      document.body.classList.remove('stripe-3ds-active');
    } finally {
      setProcessing(false);
    }
  };

  // Si changement d'organisation détecté, afficher message de redirection
  if (hasOrganizationChanged) {
    return (
      <div className="min-h-screen bg-background-secondary py-8">
        <div className="max-w-md mx-auto">
          <div className="bg-warning-10 border-2 border-warning-20 rounded-lg p-8 text-center">
            <div className="flex flex-col items-center">
              <svg
                className="w-16 h-16 mb-4"
                fill="currentColor"
                viewBox="0 0 20 20"
                style={{ color: personalization.icon.primary }}
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <h2 className="text-2xl font-bold text-warning-dark mb-2">
                {t("orderForm.errors.organizationChanged")}
              </h2>
              <p className="text-warning-dark/90 text-lg mb-4">
                {t("orderForm.errors.organizationChangedDescription")}
              </p>
              <p className="text-warning-dark/80 text-sm">
                {t("orderForm.errors.organizationChangedRedirect")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SeoTags
        title={`Paiement sécurisé | CashLive`}
        description={`Finalisez votre paiement en toute sécurité`}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        noIndex={true}
      />

      {/* In-app browser warning */}
      {showInAppWarning && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm text-amber-800 font-medium">
                {t("payment.inAppBrowserWarning.title") || "You're using an in-app browser"}
              </p>
              <p className="text-xs text-amber-700 mt-1">
                {t("payment.inAppBrowserWarning.description") || "For a smoother payment experience, you may be redirected to complete the payment verification."}
              </p>
            </div>
          </div>
        </div>
      )}



      <form onSubmit={handlePayment} className="space-y-4">
        <div
          className={`p-4 rounded-md border`}
          style={{
            backgroundColor: personalization.card.background,
            borderColor: personalization.card.border,
          }}
        >
          <h3 className="text-[11px] font-bold uppercase tracking-widest opacity-60 mb-3" style={{ color: personalization.text.heading }}>
            {t("payment.details.title")}
          </h3>

          <div className="space-y-3">
            <PaymentElement
              options={{
                layout: "tabs",
                paymentMethodOrder: ["card"],
                fields: {
                  billingDetails: {
                    name: "auto",
                    email: "auto",
                    phone: "auto",
                    address: "auto"
                  }
                },
                defaultValues: {
                  billingDetails: {
                    name: `${orderData.firstName || ""} ${orderData.lastName || ""}`.trim(),
                    email: orderData.email,
                    phone: orderData.phone,
                  },
                },
              }}
            />
          </div>

          <button
            ref={buttonRef}
            type="submit"
            disabled={!stripe || processing}
            className={`w-full py-3 px-4 mt-4 text-sm font-bold flex items-center justify-center transition-all ${personalization.button.borderRadius || 'rounded-md'}`}
            data-theme-button="primary"
          >
            {processing ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t("payment.processing")}
              </div>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                {t("payment.payNow")} {totalAmount.toFixed(2)} €
              </>
            )}
          </button>

        </div>
      </form>
    </>
  );
};

const PaymentSelection: React.FC = () => {
  const {
    selectedPickupPoint,
    orderData,
    isMergedOrder,
    currentOrderId,
    setIsOrderComplete,
  } = useOrder();
  const navigate = useNavigate();
  const { organizationId, navigateToOrder } = useNavigation();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const personalization = useOrderPersonalization();
  const { configuration, loading: isConfigLoading } = useOrgaConfigurationContext();
  const [clientSecret, setClientSecret] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAlreadyPaid, setIsAlreadyPaid] = useState(false);
  const [isCheckingPaymentStatus, setIsCheckingPaymentStatus] = useState(true); // Bloque tout pendant la vérification

  const paymentMode = configuration?.paymentMode;
  const isPayPalMode = paymentMode === 'paypal';

  // En mode stripe_api_key, le PaymentIntent est créé sur le compte Stripe de l'orga :
  // il faut utiliser UNIQUEMENT la clé publique de l'orga (configuration), sinon client_secret ne matche pas.
  const effectiveStripePublicKey = useMemo(() => {
    if (paymentMode === 'stripe_api_key') {
      return configuration?.stripeApiKeyConfig?.publishableKey ?? null;
    }
    try {
      return getStripePublicKey(organizationId || undefined);
    } catch {
      return null;
    }
  }, [paymentMode, configuration?.stripeApiKeyConfig?.publishableKey, organizationId]);

  const stripePromise = useMemo(() => {
    if (!effectiveStripePublicKey) return null;
    return loadStripe(effectiveStripePublicKey, {
      developerTools: { assistant: { enabled: isDevelopment } },
    }).catch((err) => {
      console.error('Failed to load Stripe.js:', err);
      return null;
    });
  }, [effectiveStripePublicKey]);

  // Toute autre mode que paypal ou none est considéré comme un mode primaire stripe ou autre
  const isPrimaryMode = paymentMode && paymentMode !== 'paypal' && paymentMode !== 'none';

  // Récupérer la config du provider actif dynamiquement
  const getProviderConfig = () => {
    if (!paymentMode || !configuration) return null;
    const configMap: Record<string, any> = {
      'stripe_connect': configuration.stripeConnectConfig,
      'stripe_api_key': configuration.stripeApiKeyConfig,
      'sumup': configuration.sumupConfig,
      'stancer': configuration.stancerConfig,
      'klarna': configuration.klarnaConfig,
    };
    return configMap[paymentMode] || null;
  };

  const currentConfig = getProviderConfig();
  const paypalEnabledByProvider = !!currentConfig?.paypalEnabled;

  const showPayPal = isPayPalMode || paypalEnabledByProvider;
  const showCard = !!isPrimaryMode;

  // Si on arrive sur la page de paiement sans contexte (ex: refresh), revenir à l'étape 1
  useEffect(() => {
    if (!currentOrderId) {
      console.log("⚠️ Aucun currentOrderId en contexte, redirection vers le formulaire de commande");
      navigateToOrder();
    }
  }, [currentOrderId, navigateToOrder]);

  // SÉCURITÉ CRITIQUE: Vérifier si la commande est déjà payée AVANT tout le reste
  // Si oui, empêcher l'accès à la page de paiement et rediriger vers la confirmation
  // Cette vérification bloque tout le rendu et la création de PaymentIntent
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!currentOrderId) {
        setIsCheckingPaymentStatus(false);
        return;
      }

      try {
        console.log('🔍 Vérification du statut de paiement pour la commande:', currentOrderId);
        const order = await FirebaseOrderService.getOrder(currentOrderId);

        if (!order) {
          console.log('⚠️ Commande non trouvée');
          setIsCheckingPaymentStatus(false);
          return;
        }

        // Vérifier si la commande ou ses produits sont déjà payés
        // Pour les commandes fusionnées, on vérifie le dernier produit ajouté
        const productIndex = isMergedOrder && order.products && order.products.length > 0
          ? order.products.length - 1
          : 0;

        const product = order.products?.[productIndex];
        const isProductPaid = product?.paymentInfo?.status === 'paid';
        const isDeliveryPaid = order.deliveryInfo?.paymentStatus === 'paid';

        // Si le produit actuel est déjà payé, rediriger vers la confirmation
        // IMPORTANT: On utilise navigate avec replace: true pour empêcher le retour arrière
        if (isProductPaid || (!isMergedOrder && isDeliveryPaid && order.products?.every(p => p.paymentInfo?.status === 'paid'))) {
          console.log('🔒 Commande déjà payée, redirection vers la confirmation (replace history)');
          setIsAlreadyPaid(true);
          setIsOrderComplete(true);

          // Utiliser replace: true pour remplacer l'entrée dans l'historique
          // Cela empêche l'utilisateur de revenir en arrière via le bouton du navigateur
          navigate(`/orga/${organizationId}/order/confirmation`, { replace: true });
          return;
        }

        console.log('✅ Commande non payée, autorisation d\'accès à la page de paiement');
      } catch (error) {
        console.error('Erreur lors de la vérification du statut de paiement:', error);
      } finally {
        setIsCheckingPaymentStatus(false);
      }
    };

    checkPaymentStatus();
  }, [currentOrderId, isMergedOrder, navigate, organizationId, setIsOrderComplete]);

  // Validation stricte : shippingCost doit être défini (sauf pour les commandes fusionnées)
  useEffect(() => {
    if (!currentOrderId) return; // Déjà géré par le useEffect précédent

    // Pour les commandes fusionnées, la livraison est déjà payée, donc pas besoin de shippingCost
    if (isMergedOrder) return;

    // Pour les nouvelles commandes, shippingCost DOIT être défini
    if (!orderData.deliveryMethod || orderData.shippingCost === undefined || orderData.shippingCost === null) {
      console.error('❌ PaymentSelection: shippingCost or deliveryMethod not defined', {
        deliveryMethod: orderData.deliveryMethod,
        shippingCost: orderData.shippingCost,
        isMergedOrder
      });
      toast.error(t('orderForm.errors.deliveryInfoMissing'));
      navigateToOrder();
    }
  }, [currentOrderId, orderData.deliveryMethod, orderData.shippingCost, isMergedOrder, navigateToOrder]);

  // Calculate total amount
  const getSubtotal = () => {
    if (isMergedOrder && orderData.products && orderData.products.length > 0) {
      const currentProduct = orderData.products[orderData.products.length - 1];
      return parseFloat(currentProduct.amount || "0");
    }

    if (orderData.products && orderData.products.length > 0) {
      return orderData.products.reduce((total, product) => {
        return total + parseFloat(product.amount || "0");
      }, 0);
    } else {
      return parseFloat(orderData.amount || "0");
    }
  };

  const getShippingCost = () => {
    if (isMergedOrder) return 0;
    if (!orderData.deliveryMethod) return 0;
    if (orderData.shippingCost === undefined || orderData.shippingCost === null) return 0;
    return orderData.shippingCost;
  };

  const shippingCost = isMergedOrder ? 0 : getShippingCost();
  const totalAmount = getSubtotal() + shippingCost;

  // Create payment intent when component mounts
  useEffect(() => {
    const createPaymentIntent = async () => {
      // 1. Attendre que la vérification du statut de paiement soit terminée
      if (isCheckingPaymentStatus) {
        console.log('⏳ Attente de la vérification du statut de paiement...');
        return;
      }

      // 2. Attendre que la configuration soit chargée
      if (isConfigLoading) return;

      // 3. Ne pas créer de PaymentIntent si la commande est déjà payée
      if (isAlreadyPaid) {
        console.log('🚫 PaymentIntent non créé: commande déjà payée');
        setIsLoading(false);
        return;
      }

      // 4. On ne crée un PaymentIntent Stripe que si showCard est vrai
      if (!showCard || !currentOrderId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        let productIndex = 0;
        if (isMergedOrder && orderData.products && orderData.products.length > 0) {
          productIndex = orderData.products.length - 1;
        }

        const productsAmountInCents = Math.round(getSubtotal() * 100);
        const shippingCostInCents = Math.round(shippingCost * 100);
        const totalAmountInCents = productsAmountInCents + shippingCostInCents;

        const response = await createPaymentIntentDirect({
          organizationId: organizationId || '',
          amount: totalAmountInCents,
          currency: "eur",
          customerEmail: orderData.email || currentUser?.email || '',
          customerName: `${orderData.firstName || ''} ${orderData.lastName || ''}`.trim(),
          description: `Paiement pour commande ${currentOrderId}`,
          metadata: {
            orderId: currentOrderId,
            productIndex: productIndex?.toString() || '0',
            productsAmount: productsAmountInCents.toString(),
            shippingCost: shippingCostInCents.toString(),
          },
        });

        if (response.clientSecret) {
          setClientSecret(response.clientSecret);
        }
      } catch (error) {
        console.error('Error creating payment intent:', error);
      } finally {
        setIsLoading(false);
      }
    };

    createPaymentIntent();
  }, [isCheckingPaymentStatus, isAlreadyPaid, isConfigLoading, showCard, currentOrderId, organizationId, isMergedOrder, orderData, currentUser]);

  // Check if we have delivery information (either pickup point or address)
  // deliveryMethod can be either a standard method name or a SendCloud method ID (string number)
  const hasDeliveryInfo =
    selectedPickupPoint ||
    (orderData.address && orderData.deliveryMethod) ||
    (orderData.deliveryMethod && orderData.deliveryMethod.trim() !== '');

  // Redirect to order if no delivery info (in useEffect to avoid render-time navigation)
  useEffect(() => {
    if (!hasDeliveryInfo && currentOrderId) {
      console.log('⚠️ No delivery info found, redirecting to order');
      console.log('📦 Current state:', {
        selectedPickupPoint,
        deliveryMethod: orderData.deliveryMethod,
        address: orderData.address,
      });
      navigateToOrder();
    }
  }, [hasDeliveryInfo, currentOrderId, navigateToOrder, selectedPickupPoint, orderData.deliveryMethod, orderData.address]);

  if (!hasDeliveryInfo) {
    return null;
  }

  // PRIORITÉ 1: Vérification du statut de paiement en cours
  // Bloque tout le rendu jusqu'à ce qu'on sache si la commande est déjà payée
  if (isCheckingPaymentStatus) {
    return (
      <div className="flex items-center justify-center p-8">
        <svg
          className="animate-spin h-8 w-8"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          style={{ color: personalization.icon.primary }}
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="ml-2" style={{ color: personalization.text.secondary }}>
          {t("payment.checking") || "Vérification en cours..."}
        </span>
      </div>
    );
  }

  // PRIORITÉ 2: Si la commande est déjà payée, afficher un message de redirection
  if (isAlreadyPaid) {
    return (
      <div className="flex items-center justify-center p-8">
        <svg
          className="animate-spin h-8 w-8"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          style={{ color: personalization.icon.primary }}
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="ml-2" style={{ color: personalization.text.secondary }}>
          {t("payment.redirectingToConfirmation") || "Redirection vers la confirmation..."}
        </span>
      </div>
    );
  }

  if (isLoading || isConfigLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <svg
          className="animate-spin h-8 w-8"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          style={{ color: personalization.icon.primary }}
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="ml-2" style={{ color: personalization.text.secondary }}>
          {showPayPal && !showCard ? t("payment.loading.paypal") || "Préparation de PayPal..." : t("payment.loading.preparing")}
        </span>
      </div>
    );
  }

  // Si on a le mode carte mais pas de clientSecret après chargement
  if (showCard && !clientSecret && !isLoading) {
    return (
      <div className="text-center p-8">
        <p className="text-error">{t("payment.errors.intentCreation")}</p>
        <button onClick={() => navigate(0)} className="mt-4 button-primary">
          {t("common.retry")}
        </button>
      </div>
    );
  }

  // Elements options for Stripe
  const elementsOptions = {
    clientSecret,
    locale: "fr" as const,
    appearance: {
      theme: "stripe" as const,
      variables: {
        fontFamily: "system-ui, sans-serif",
      },
    },
  };

  return (
    <>
      <style>{`
        button[data-theme-button="primary"]:not(:disabled) {
          color: ${personalization.button.primaryText || '#FFFFFF'} !important;
          ${(personalization.button.primary && (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient')))
          ? `background: ${personalization.button.primary} !important;`
          : `background-color: ${personalization.button.primary || '#9333EA'} !important;`}
        }
        button[data-theme-button="primary"]:hover:not(:disabled) {
          color: ${personalization.button.primaryText || '#FFFFFF'} !important;
          ${(personalization.button.primaryHover && (personalization.button.primaryHover.includes('gradient') || personalization.button.primaryHover.includes('linear-gradient')))
          ? `background: ${personalization.button.primaryHover} !important;`
          : `background-color: ${personalization.button.primaryHover || '#7E22CE'} !important;`}
        }
        button[data-theme-button="primary"]:active:not(:disabled) {
          color: ${personalization.button.primaryText || '#FFFFFF'} !important;
        }
        button[data-theme-button="primary"]:focus:not(:disabled) {
          color: ${personalization.button.primaryText || '#FFFFFF'} !important;
          outline: 2px solid ${personalization.button.primary || '#9333EA'};
          outline-offset: 2px;
        }
        button[data-theme-button="primary"]:disabled {
          color: ${personalization.button.disabledText || '#6B7280'} !important;
          background-color: ${personalization.button.disabledBackground || '#D1D5DB'} !important;
          background-image: none !important;
          background: ${personalization.button.disabledBackground || '#D1D5DB'} !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
      `}</style>
      <div className="animate-fade-in max-w-2xl mx-auto py-2 px-4 lg:py-6 space-y-6">
        {/* Order Summary at the top */}
        <OrderSummary />

        {/* Main Payment Container */}
        <div className="space-y-4">
          <div
            className={`p-4 md:p-6 ${personalization.card.borderRadius || 'rounded-lg'} ${personalization.card.shadow || 'shadow-sm'} border`}
            style={{
              backgroundColor: personalization.card.background,
              borderColor: personalization.card.border,
            }}
          >
            <h2
              className="text-lg font-bold mb-4"
              style={{ color: personalization.text.heading }}
            >
              {t("payment.securePayment")}
            </h2>

            <div className="space-y-6">
              {/* PayPal Payment Section (on top if enabled) */}
              {showPayPal && configuration?.paypalConfig?.clientId && (
                <div id="paypal-payment-container">
                  <PayPalPaymentForm
                    clientId={configuration?.paypalConfig?.clientId || ""}
                    organizationId={organizationId || ""}
                    amount={totalAmount}
                    isExtension={paypalEnabledByProvider}
                  />
                </div>
              )}

              {/* Stripe Payment Section — stripePromise doit être celui du compte qui a créé le PaymentIntent (orga en API key, Cashlive en Connect) */}
              {showCard && clientSecret && !stripePromise && (
                <div className="text-center p-4 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-200">
                  <p>{t("payment.errors.stripeKeyMissing") || "Clé publique Stripe manquante pour cette organisation. Veuillez contacter le vendeur."}</p>
                </div>
              )}
              {showCard && clientSecret && stripePromise && (
                <div id="stripe-payment-container">
                  {/* Subtle Separator if both are shown */}
                  {showPayPal && (
                    <div className="relative flex py-3 items-center">
                      <div className="flex-grow border-t border-border"></div>
                      <span className="flex-shrink mx-4 text-xs font-bold uppercase tracking-widest opacity-60" style={{ color: personalization.text.secondary }}>
                        {t("common.or") === "common.or" ? "OU" : t("common.or")}
                      </span>
                      <div className="flex-grow border-t border-border"></div>
                    </div>
                  )}
                  <Elements stripe={stripePromise} options={elementsOptions}>
                    <CheckoutForm clientSecret={clientSecret} />
                  </Elements>
                </div>
              )}

              {!showCard && !showPayPal && (
                <div className="text-center p-6 bg-surface rounded-xl border border-border">
                  <p className="text-foreground-secondary">Aucun moyen de paiement n'est configuré.</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-start">
            <button
              type="button"
              onClick={() => navigateToOrder()}
              className={`flex items-center transition-colors py-2 px-4 text-sm rounded-md border font-semibold ${personalization.button.borderRadius || 'rounded-md'}`}
              style={{
                backgroundColor: personalization.card.background,
                borderColor: personalization.card.border,
                color: personalization.text.secondary,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = personalization.card.background;
                e.currentTarget.style.color = personalization.text.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = personalization.card.background;
                e.currentTarget.style.color = personalization.text.secondary;
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" style={{ color: 'inherit' }} />
              {t("common.back")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSelection;
